import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Save, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { OPS } from "@/constants/testIds";
import { WM_TIMEOUT_MS, wmMessage } from "@/lib/wmError";
import { clearDraft, draftKey, loadDraft, newCrid, saveDraft } from "@/lib/wmDraft";
import { QuantityInput } from "@/components/waste-meals/QuantityInput";
import { ItemPicker } from "@/components/waste-meals/ItemPicker";
import { NOTES_MAX, countsAsWaste, isNonLossReason } from "@/components/waste-meals/wmUtils";

const C = OPS.wm;

/**
 * Quick add. At phone widths the form shows exactly: item, quantity with the
 * unit as a suffix, reason (waste only), save. Date and notes sit behind
 * «المزيد», with the date already defaulted correctly.
 */
export function QuickEntryForm({ scope, catalogue, branchId, date, type, onDateChange, onSaved }) {
    const { t, lang } = useI18n();
    const { me } = useAuth();
    const userId = me?.user?.id;

    const dkey = draftKey({ userId, branchId, date, type, kind: "quick" });
    const [itemId, setItemId] = useState("");
    const [qty, setQty] = useState("");
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [more, setMore] = useState(false);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    const crid = useRef(newCrid());

    useEffect(() => {
        const d = loadDraft(dkey);
        if (d) {
            setItemId(d.itemId || ""); setQty(d.qty || "");
            setReason(d.reason || ""); setNotes(d.notes || "");
        }
    }, [dkey]);

    useEffect(() => {
        if (itemId || qty) saveDraft(dkey, { itemId, qty, reason, notes });
    }, [dkey, itemId, qty, reason, notes]);

    const item = useMemo(
        () => (catalogue?.items || []).find((i) => i.id === itemId), [catalogue, itemId]);
    const unit = item ? (lang === "ar" ? item.unit_name_ar : item.unit_name_en) : "";

    const touch = (fn) => (v) => { setFailed(false); crid.current = newCrid(); fn(v); };

    const save = async () => {
        if (!itemId || !qty) return;
        setBusy(true);
        try {
            await api.post("/waste-meals/records", {
                branch_id: branchId, date, type, item_id: itemId, quantity: qty,
                reason_key: type === "waste" ? (reason || null) : null,
                notes: notes || null, client_request_id: crid.current,
            }, { timeout: WM_TIMEOUT_MS });
            toast.success(t("wm_saved"));
            setItemId(""); setQty(""); setNotes(""); setFailed(false);
            // The reason stays for the next entry — except «مرتجع للمستودع»: a
            // sticky non-loss reason would quietly take the next real waste out
            // of every total (D2, the same trap as the day sheet's default).
            if (isNonLossReason(catalogue?.reasons, reason)) setReason("");
            crid.current = newCrid();
            clearDraft(dkey);
            onSaved && (await onSaved());
        } catch (e) {
            const msg = wmMessage(e, lang);
            if (msg) { toast.error(msg); } else { setFailed(true); }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={C.quick.form}
             className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3 shadow-[var(--shadow-sm)]">
            <ItemPicker catalogue={catalogue} value={itemId} onChange={touch(setItemId)}
                        branchId={branchId} type={type} />
            <QuantityInput testId={C.quick.qty} value={qty} onChange={touch(setQty)}
                           unitLabel={unit} allowsDecimal={!!item?.allows_decimal}
                           warnAbove={scope?.settings?.warn_quantity} onEnter={save} />
            {type === "waste" ? (
                <Select value={reason} onValueChange={touch(setReason)}>
                    <SelectTrigger data-testid={C.quick.reason} className="h-10">
                        <SelectValue placeholder={t("wm_reason")} />
                    </SelectTrigger>
                    <SelectContent>
                        {(catalogue?.reasons || []).map((r) => (
                            <SelectItem key={r.key} value={r.key}>
                                {lang === "ar" ? r.name_ar : r.name_en}
                                {!countsAsWaste(r) ? (
                                    <span className="ms-1.5 text-[10px] font-semibold text-[color:var(--brand-brown)]">
                                        · {t("wm_reason_not_waste")}
                                    </span>
                                ) : null}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : null}

            <button type="button" data-testid={C.moreToggle} onClick={() => setMore((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--brand-brown)]">
                {more ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                {more ? t("wm_less") : t("wm_more")}
            </button>
            {more ? (
                <div className="space-y-2">
                    <Input data-testid={C.dateInput} type="date" value={date} dir="ltr" lang="ar"
                           min={scope?.min_date || undefined} max={scope?.today}
                           onChange={(e) => onDateChange(e.target.value)} className="h-10" />
                    <Textarea data-testid={C.quick.notes} rows={2} value={notes} maxLength={NOTES_MAX}
                              onChange={(e) => touch(setNotes)(e.target.value)} placeholder={t("wm_notes")}
                              className="text-base" />
                </div>
            ) : null}

            {failed ? (
                <div data-testid={C.offlineError}
                     className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:var(--danger-soft,rgba(220,38,38,0.08))] px-3 py-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                        <WifiOff className="h-3.5 w-3.5" />
                        {t("wm_offline_error")}
                    </span>
                    <Button size="sm" variant="outline" data-testid={C.retry} onClick={save} disabled={busy}>
                        {t("wm_retry")}
                    </Button>
                </div>
            ) : null}

            <Button data-testid={C.quick.save} onClick={save} disabled={busy || !itemId || !qty}
                    className="h-11 w-full gap-2 text-sm font-bold">
                <Save className="h-4 w-4" />
                {busy ? t("wm_saving") : t("wm_save")}
            </Button>
        </div>
    );
}
